import BuildingLookup from "components/Buildings/SelectDynamic";
import { Input } from "components/Inputs";
// import PropertyLookup from "components/Properties/Lookup";
import { Button, Col, Row } from "components/Shared";
import FloorLookup from "components/Tenancies/FloorSelectDynamic";
import TenancyLookup from "components/Tenancies/TenancySelectDynamic";
import AreaMeasurements from "components/Tenancies/AreaMeasurements";
import { NewProjectContext } from "context";
import _ from "lodash";
import React, { useCallback, useContext, useEffect, useState } from "react";
import validate from "validate.js";

const constraints = {
	name: {
		presence: {
			message: "^ is required",
		},
	},
};
// The lease area IN FORCE TODAY, i.e. Tenancy#lease_area_sqm — the latest entry in
// the dated `as_of_date_area_measurement` series whose AsOfDate is not in the
// future. Undefined when the API did not return the field (the new-tenancy
// wizard's sparse fieldset omits it) and null when the unit has only
// future-dated measurements; both mean "fall back to the stored column".
const effectiveLeaseArea = (tenancy) => _.get(tenancy, "lease_area_sqm");

const Form = ({ tenancy = {}, onUpdate, oldBuildingId }) => {
	// The "Area (Lease Area)" input DISPLAYS the effective lease area rather than
	// the legacy `tenancies.area` column, which is routinely blank/0.0 on
	// Yardi-fed units while the dated series carries the real number (MW001: area
	// blank, lease area 36.3 m² as of 1 Jan 2007) — reading the column alone
	// rendered an empty box next to a populated measurements table.
	//
	// It is seeded into `data.area` because that is the key the input reads
	// (`name="area"`). On the edit form the input is LOCKED and #onSubmit omits
	// `area` from the payload, so this value is shown but never written back.
	const [data, setData] = useState(() => {
		const leaseArea = effectiveLeaseArea(tenancy);
		return _.isNil(leaseArea) ? tenancy : _.assign({}, tenancy, { area: leaseArea });
	});
	const newProject = useContext(NewProjectContext);
	const template = _.get(newProject, "template", false);
	const buildings = _.get(data, "property.buildings", []);
	const building = _.find(buildings, { id: _.get(data, "building_id") });
	const tenancies = _.filter(_.get(building, "tenancies", []), { floor_id: _.get(data, "floor_id") });

	const getTenancyKindOptions = useCallback(
		() => [
			!!data.tenancy_exist && "existing_tenancy",
			"new_tenancy",
			!_.includes(_.get(template, "tags"), "flinders") ? "unallocated_space" : false,
		],
		[data.tenancy_exist, template]
	);
	const [tenancyKindOptions, setTenancyKindOptions] = useState(() => getTenancyKindOptions());

	useEffect(() => {
		setTenancyKindOptions(() => getTenancyKindOptions());
	}, [getTenancyKindOptions]);

	// const onChangeProperty = (property) =>
	// 	setData((prevData) =>
	// 		_.assign({}, prevData, {
	// 			property: property,
	// 			building_id: null,
	// 			floor_id: null,
	// 			tenancy_exist: false,
	// 			// id: null,
	// 			tenancy_kind: null,
	// 		})
	// 	);

	const onChangeBuilding = (building_id) =>
		setData((prevData) =>
			_.assign({}, prevData, {
				building_id: building_id,
				floor_id: null,
				tenancy_exist: false,
				// id: null,
				tenancy_kind: null,
			})
		);

	const onChangeFloor = (floor_id, tenancy_exist) =>
		setData((prevData) =>
			_.assign({}, prevData, {
				floor_id: floor_id,
				tenancy_exist: tenancy_exist,
				// id: null,
				tenancy_kind: null,
			})
		);

	const onChangeTenancy = (tenancy_id) => setData((prevData) => _.assign({}, prevData, { id: tenancy_id }));

	const onSubmit = () => {
		if (_.get(data, "tenancy_kind") === "unallocated_space") data["name"] = "Unallocated Space";

		if (_.get(data, "tenancy_kind") === "existing_tenancy" && !!_.get(data, "id")) {
			onUpdate(
				_.assign(
					{},
					_.find(tenancies, { id: data.id }),
					_.assign({}, data, {
						building: _.find(buildings, { id: data.building_id }),
					})
				)
			);
		} else if (_.get(data, "tenancy_kind") === "existing_tenancy" && !_.get(data, "id")) {
			setData(_.assign({}, data, {}));
		} else if (_.get(data, "tenancy_kind") !== "existing_tenancy") {
			const errors = validate(data, constraints);
			// `area` is omitted on the edit form: the input there is locked and shows
			// the effective LEASE area, not the `tenancies.area` column, so posting it
			// back would silently overwrite the column with a number the user could
			// not edit. A disabled HTML input is not submitted either — same contract.
			// The wizard still sends it, since that is where an area is genuinely set.
			const payload = isEditForm ? _.omit(data, "area") : data;
			!!errors ? setData(_.assign({}, data, { errors })) : onUpdate(payload);
		}
	};

	const propertyId = _.get(data, "property.id") ? _.get(data, "property.id") : data.property_id;

	// True on the tenancy edit route (existing record, not the new-tenancy wizard).
	const isEditForm = !!_.get(data, "id") && !_.get(data, "tenancy_kind");
	const updateField = (field) => (value) => setData((prevData) => _.assign({}, prevData, { [field]: value }));

	// console.log("EWWEWE", _.get(data, "building_id"), { oldBuildingId });

	return (
		<Row>
			<Col>
				{/* <PropertyLookup onChange={onChangeProperty} name="property" defaultState={{ data }} /> */}
				<BuildingLookup propertyId={propertyId} onChange={onChangeBuilding} preselectedBuildingId={oldBuildingId} />
				<FloorLookup buildingId={_.get(data, "building_id")} onChange={onChangeFloor} />
				<Input
					onChange={(tenancy_kind) => setData((prevData) => _.assign({}, prevData, { tenancy_kind }))}
					name="tenancy_kind"
					defaultState={data}
					required={constraints}
					options={tenancyKindOptions}
					showIf={!!_.get(data, "property")}
				/>
				{_.get(data, "tenancy_kind") === "existing_tenancy" && (
					<TenancyLookup floorId={_.get(data, "floor_id")} onChange={onChangeTenancy} />
				)}
				<Input
					onChange={(name) => setData(_.assign({}, data, { name }))}
					name="name"
					defaultState={data}
					required={constraints}
					label={_.includes(_.get(template, "tags"), "flinders") ? "room_number" : "tenancy_name"}
					// showIf={_.get(data, "tenancy_kind") === "new_tenancy"}
				/>
				{/* AREA — read-only on the edit form, editable in the new-tenancy wizard.
				    `label` is a dictionary.json key, not literal text (Inputs/Label.tsx
				    runs it through translate()).

				    `disabled` renders the value as a static, non-interactive line and
				    `is-locked` greys it — the same pairing Templates/Modal.tsx uses for a
				    field that must not be edited (`#app .form-group.is-locked` in
				    _form.scss). Locked because Yardi owns these numbers: /unit_data is the
				    only writer of the dated series this value comes from.

				    Left editable in the wizard: a tenancy being created has no
				    measurement to show, so it needs a real input. */}
				<Input
					onChange={(area) => setData(_.assign({}, data, { area }))}
					name="area"
					label="area_lease_area"
					disabled={isEditForm}
					className={isEditForm ? "is-locked" : undefined}
					defaultState={data}
					required={constraints}
					suffix="m²"
					type="number"
					// showIf={_.get(data, "tenancy_kind") === "new_tenancy" || _.get(data, "tenancy_kind") === "unallocated_space"}
				/>
				<Input
					onChange={(kind) => setData(_.assign({}, data, { kind }))}
					name="kind"
					defaultState={data}
					required={constraints}
					options={["speciality", "food", "kiosk", "food-kiosk"]}
					showIf={
						(_.get(data, "tenancy_kind") === "new_tenancy" || _.get(data, "tenancy_kind") === "unallocated_space") &&
						_.includes(_.get(template, "tags"), "retail")
					}
				/>
				{/* `label="unit_type"` rather than the default `kind` key: `kind` is
				    shared with Lease/Project/Template/Inspection/Company, so relabelling
				    it would rename their fields too. */}
				<Input onChange={updateField("kind")} name="kind" label="unit_type" defaultState={data} showIf={isEditForm} />
				{/* Yardi unit fields — Units[].UnitCode / RemovedFlag / RelocationStatus.
				    Editable, but note a later /lease_data or /unit_data sync rewrites all
				    three from the payload, and `unit_code` is what the importer matches
				    units on.

				    UnitStatus is NOT here: it is lease-level and moved onto Lease, so it
				    is edited in components/Leases/Form.tsx. */}
				<Input onChange={updateField("unit_code")} name="unit_code" defaultState={data} showIf={isEditForm} />
				<Input onChange={updateField("removed_flag")} name="removed_flag" defaultState={data} showIf={isEditForm} />
				<Input
					onChange={updateField("relocation_status")}
					name="relocation_status"
					defaultState={data}
					showIf={isEditForm}
				/>
				{/* "UnitOccStatus" — Yardi's occupancy status for the UNIT, off the
				    /unit_data payload and the unit master xlsx (Sim, 4 Sep 2026). Its own
				    column on `tenancies`, and NOT the lease-level UnitStatus above. The
				    label carries the Yardi name because "occupancy status" already means
				    the FOLDED answer on the list beside it (Tenancy#occupancy_status),
				    and the two disagree freely. */}
				<Input
					onChange={updateField("unit_occ_status")}
					name="unit_occ_status"
					defaultState={data}
					showIf={isEditForm}
				/>
				<Input onChange={updateField("country")} name="country" defaultState={data} showIf={isEditForm} />
				<Input onChange={updateField("address")} name="address" defaultState={data} showIf={isEditForm} />
				<Input onChange={updateField("zone")} name="zone" defaultState={data} showIf={isEditForm} />
				<Input onChange={updateField("sub_zone")} name="sub_zone" defaultState={data} showIf={isEditForm} />
				<Input onChange={updateField("portfolio")} name="portfolio" defaultState={data} showIf={isEditForm} />
				<Input onChange={updateField("location")} name="location" defaultState={data} showIf={isEditForm} />
				<Input onChange={updateField("area_id")} name="area_id" defaultState={data} showIf={isEditForm} />
				{/* actual_area_sqft/sqm, lease_area_sqft/sqm and as_of_date are NOT
				    editable here any more. They stopped being columns and are derived
				    from the dated series Yardi sends (tenancies.as_of_date_area_measurement,
				    shown as a table on the project's Tenancies tab): a single typed
				    value has no AsOfDate to belong to, and the API no longer permits
				    the params. */}
				{/* "UnitExclude" — READ-ONLY, AND "Yes"/"No" RATHER THAN A CHECKBOX
				    (Sim, 4 Sep 2026).

				    It is Yardi's, not ours: the flag arrives on /unit_data and the unit
				    master xlsx, and setting it true is what SOFT-DELETES the unit
				    (V2::CompaniesController#unit_data -> Tenancy#soft_delete!). Ticking a
				    box here only ever wrote the column, so the form could say a unit was
				    excluded while the unit stayed live and listed — two answers to one
				    question. Removing the unit is Yardi's call, so this only reports it.

				    `disabled` makes Input render a static <p> with no form control at all,
				    so nothing is submitted and the API's `exclude` param is untouched by
				    this form, and `is-locked` greys it — the same pairing "Area (Lease
				    Area)" above uses, and the one `#app .form-group.is-locked` in
				    _form.scss styles. `disabled` alone renders bare text with no box.

				    `name="unit_exclude"` for the label alone: `exclude` is a shared
				    dictionary key ("Exclude") and relabelling it would rename it wherever
				    else it is used — the same reason `kind` is relabelled per form above.
				    Nothing reads the name otherwise, since a disabled field posts nothing.

				    NOT STATED READS "No": nil means Yardi never sent the flag, and a unit
				    that is on this screen at all has not been excluded. */}
				<Input
					name="unit_exclude"
					defaultState={{ unit_exclude: !!_.get(data, "exclude") ? "Yes" : "No" }}
					disabled
					className="is-locked"
					showIf={isEditForm}
				/>
				<Button onClick={onSubmit} />
			</Col>
			{/* Read-only, beside the inputs rather than under them: these are the
			    numbers the fields above USED to hold, and the only place the unit's
			    history (and anything Yardi has dated into the future) is visible.
			    Its own column only when there is something to show, so the new-tenancy
			    form keeps its full width. */}
			{!_.isEmpty(_.get(data, "area_measurements")) && (
				<Col>
					<AreaMeasurements measurements={_.get(data, "area_measurements")} />
				</Col>
			)}
		</Row>
	);
};

export default Form;
